"use client";

import { Checkbox, LinkifyText } from "@/components";
import { BundleOffer as BundleOfferType } from "@/queries/get-cohort-details.types";
import cx from "@/utils/cx";
import Image from "next/image";
import React, { useCallback, useContext } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  Path,
  useForm,
  useFormContext,
} from "react-hook-form";
import { dataContext } from "@/contexts/cohort-data";

const BundleOffer = ({
  active,
  offer,
  currency,
}: {
  active?: boolean;
  offer: BundleOfferType;
  currency: string;
}) => {
  const { control } = useFormContext();

  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
  });

  return (
    <div
      tabIndex={0}
      className={cx(
        "py-2 px-4 rounded-xl relative border-2 transition-colors border-dashed w-full cursor-pointer",
        {
          "border-primary hover:border-primary": active,
          "border-primary/30 hover:border-primary/50": !active,
        }
      )}
    >
      {active && (
        <span className="absolute text-[10px] -top-3 left-4 rounded-md py-1 px-2 bg-primary text-white">
          Applied
        </span>
      )}
      <div className="flex justify-between items-center gap-3 mb-2">
        <Checkbox
          control={control}
          name={offer.id}
          label={offer.label}
          labelClassName="font-semibold peer-disabled:opacity-100"
          containerClassName="pointer-events-none flex-1"
          checked={active}
          disabled
          className="disabled:opacity-100"
        />
        <div className="shrink-0 flex flex-col -space-y-2">
          <s className="text-sm">{formatter.format(offer.originalPrice)}</s>
          <div className="inline-flex items-center">
            <p className="text-lg font-bold">{formatter.format(offer.price)}</p>
          </div>
        </div>
      </div>
      <div className="flex gap-3 items-center">
        {offer.image ? (
          <Image
            src={offer.image}
            width={100}
            height={100}
            sizes="100px"
            className="shrink-0 object-contain h-[100px]"
            alt={offer.label}
          />
        ) : null}
        {offer.description ? (
          <LinkifyText className="grow text-sm text-left whitespace-break-spaces">
            {offer.description}
          </LinkifyText>
        ) : null}
      </div>
      {offer.note && (
        <p className="bg-neutral-100 rounded-md text-xs py-1.5 px-2 mt-3 whitespace-break-spaces">
          {offer.note}
        </p>
      )}
    </div>
  );
};

const BundleOffers = ({
  onChange,
  value,
}: {
  onChange?: (offers: any[]) => {};
  value?: any[];
}) => {
  const { data } = useContext(dataContext);

  const currency = data?.currency || "INR";

  const handleClickBundleOffer = useCallback(
    (id: string) => {
      const offers =
        value?.map((offer) => {
          if (offer.id === id) {
            return { ...offer, isApplied: !offer.isApplied };
          }
          return offer;
        }) ?? [];
      onChange && onChange(offers);
    },
    [onChange, value]
  );

  const formFields = useForm();

  return (
    <FormProvider {...formFields}>
      <ul className="grid gap-4">
        {value?.map((offer) => {
          return (
            <li key={offer.id} onClick={() => handleClickBundleOffer(offer.id)}>
              <BundleOffer
                currency={currency}
                active={offer.isApplied}
                offer={offer}
              />
            </li>
          );
        })}
      </ul>
    </FormProvider>
  );
};

function BundleOffersController<T extends FieldValues>({
  name,
  code,
}: {
  name: Path<T>;
  code?: string;
}) {
  return (
    <Controller
      name={name}
      render={({ field: { onChange, value } }) => (
        // @ts-ignore
        <BundleOffers onChange={onChange} value={value} code={code} />
      )}
    />
  );
}

export default BundleOffersController;
